/**
 * Tempo no fuso DA ARENA — nunca no do servidor, nunca no do navegador.
 *
 * O banco guarda instantes (`timestamptz`); a pessoa pensa em "terça às
 * 19h". A conversão entre os dois acontece na BORDA — na entrada da API
 * e na apresentação —, e sempre com o fuso configurado na arena. Nunca
 * no meio de uma regra.
 *
 * É isso que evita a classe de bug clássica da agenda: a aula das 19h
 * aparecendo às 22h porque o servidor roda em UTC, ou uma reserva de
 * domingo caindo no sábado depois de uma virada de horário de verão.
 * O Brasil não tem horário de verão desde 2019, mas o fuso é
 * configuração da arena e o código não pode apostar nisso.
 *
 * Mora em @arena/shared porque os dois lados precisam dele: o backend
 * converte "2026-09-02 19:00" em instante ao gravar; o frontend
 * converte o instante de volta em "19:00" ao desenhar a grade — e o faz
 * no fuso da arena mesmo quando o navegador está em Lisboa.
 *
 * Só `Intl`, sem biblioteca: o Node 22 e todo navegador moderno trazem
 * a base de fusos completa.
 *
 * Ver: Arena_backend/Docs/plano/06-agenda-quadras.md
 */
const MINUTO_MS = 60_000;
const DIA_MS = 24 * 60 * MINUTO_MS;
const formatadores = new Map();
/**
 * Um formatador por fuso, reaproveitado. Construir `Intl.DateTimeFormat`
 * é caro, e a grade de um dia chama isto dezenas de vezes.
 *
 * `hourCycle: 'h23'` e não `hour12: false`: a segunda forma devolve
 * "24:00" para a meia-noite em alguns motores.
 */
function formatador(fuso) {
    let existente = formatadores.get(fuso);
    if (!existente) {
        existente = new Intl.DateTimeFormat('en-US', {
            timeZone: fuso,
            hourCycle: 'h23',
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
        });
        formatadores.set(fuso, existente);
    }
    return existente;
}
function camposNoFuso(ms, fuso) {
    const campos = { ano: 0, mes: 0, dia: 0, hora: 0, minuto: 0, segundo: 0 };
    for (const parte of formatador(fuso).formatToParts(new Date(ms))) {
        const valor = Number(parte.value);
        switch (parte.type) {
            case 'year':
                campos.ano = valor;
                break;
            case 'month':
                campos.mes = valor;
                break;
            case 'day':
                campos.dia = valor;
                break;
            case 'hour':
                campos.hora = valor;
                break;
            case 'minute':
                campos.minuto = valor;
                break;
            case 'second':
                campos.segundo = valor;
                break;
        }
    }
    return campos;
}
/** O relógio de parede lido como se fosse UTC. Base das contas abaixo. */
function paredeComoUtc(ms, fuso) {
    const c = camposNoFuso(ms, fuso);
    return Date.UTC(c.ano, c.mes - 1, c.dia, c.hora, c.minuto, c.segundo);
}
const dois = (n) => String(n).padStart(2, '0');
function paraMs(instante) {
    return instante instanceof Date ? instante.getTime() : new Date(instante).getTime();
}
/** Um instante, lido no relógio de parede do fuso. */
export function partesNoFuso(instante, fuso) {
    const c = camposNoFuso(paraMs(instante), fuso);
    const data = `${c.ano}-${dois(c.mes)}-${dois(c.dia)}`;
    return {
        data,
        hora: `${dois(c.hora)}:${dois(c.minuto)}`,
        minutosDoDia: c.hora * 60 + c.minuto,
        diaDaSemana: diaDaSemana(data),
    };
}
/** "19:00" de um instante, no fuso da arena. */
export function horaNoFuso(instante, fuso) {
    return partesNoFuso(instante, fuso).hora;
}
/**
 * O instante em que o relógio de parede do fuso marca `data hora`.
 *
 * As duas bordas do horário de verão têm resposta definida, a mesma do
 * `Temporal` com `disambiguation: 'compatible'`:
 *
 *   hora que NÃO EXISTE  (o relógio pula de 00:00 para 01:00)
 *                        → o instante logo depois do pulo: 01:00
 *   hora que existe DUAS VEZES (o relógio volta de 00:00 para 23:00)
 *                        → a primeira das duas
 *
 * O método: testar o deslocamento de antes e o de depois de uma
 * eventual virada (um dia para cada lado) e ficar com o que reproduz o
 * relógio pedido. Supõe no máximo uma virada em 48 horas — verdade para
 * todo fuso real.
 */
export function instanteLocal(data, hora, fuso) {
    const [ano, mes, dia] = data.split('-').map(Number);
    const [h, m] = hora.split(':').map(Number);
    const parede = Date.UTC(ano, mes - 1, dia, h, m);
    const deslocamentoAntes = paredeComoUtc(parede - DIA_MS, fuso) - (parede - DIA_MS);
    const deslocamentoDepois = paredeComoUtc(parede + DIA_MS, fuso) - (parede + DIA_MS);
    const candidatoAntes = parede - deslocamentoAntes;
    const candidatoDepois = parede - deslocamentoDepois;
    const antesServe = paredeComoUtc(candidatoAntes, fuso) === parede;
    const depoisServe = paredeComoUtc(candidatoDepois, fuso) === parede;
    if (antesServe && depoisServe)
        return new Date(Math.min(candidatoAntes, candidatoDepois));
    if (antesServe)
        return new Date(candidatoAntes);
    if (depoisServe)
        return new Date(candidatoDepois);
    // A hora caiu no buraco da virada. Com o deslocamento de antes, o
    // instante resultante aparece no relógio já depois do pulo.
    return new Date(candidatoAntes);
}
/**
 * O dia local inteiro, como intervalo de instantes `[inicio, fim)`.
 *
 * Não é sempre 24 horas: no dia de uma virada de horário de verão ele
 * tem 23 ou 25. Somar 24h ao início, em vez de calcular a meia-noite
 * seguinte, é exatamente o bug que este arquivo existe para evitar.
 */
export function intervaloDoDia(data, fuso) {
    return {
        inicio: instanteLocal(data, '00:00', fuso),
        fim: instanteLocal(adicionarDias(data, 1), '00:00', fuso),
    };
}
/** A data de hoje no fuso — que não é a de hoje em UTC depois das 21h. */
export function hojeNoFuso(fuso, agora = new Date()) {
    return partesNoFuso(agora, fuso).data;
}
// ---------------------------------------------------------------------
// Calendário puro — datas "AAAA-MM-DD", sem fuso nem instante.
// ---------------------------------------------------------------------
/** Aritmética de calendário: `adicionarDias('2026-02-28', 1)` → '2026-03-01'. */
export function adicionarDias(data, dias) {
    const [ano, mes, dia] = data.split('-').map(Number);
    const d = new Date(Date.UTC(ano, mes - 1, dia + dias));
    return `${d.getUTCFullYear()}-${dois(d.getUTCMonth() + 1)}-${dois(d.getUTCDate())}`;
}
/** 0 = domingo … 6 = sábado. */
export function diaDaSemana(data) {
    const [ano, mes, dia] = data.split('-').map(Number);
    return new Date(Date.UTC(ano, mes - 1, dia)).getUTCDay();
}
/** Dias entre duas datas de calendário, `b - a`. */
export function diasEntre(a, b) {
    const ms = (data) => {
        const [ano, mes, dia] = data.split('-').map(Number);
        return Date.UTC(ano, mes - 1, dia);
    };
    return Math.round((ms(b) - ms(a)) / DIA_MS);
}
/** "19:30" → 1170. */
export function minutosDe(hora) {
    const [h, m] = hora.split(':').map(Number);
    return h * 60 + m;
}
/**
 * 1170 → "19:30". 1440 é a meia-noite do fim do dia, e a hora da GRADE
 * passa dela: 1500 é "25:00", a 01:00 da madrugada seguinte.
 */
export function horaDe(minutos) {
    return `${dois(Math.floor(minutos / 60))}:${dois(minutos % 60)}`;
}
/** O fuso existe na base IANA deste runtime? */
export function fusoValido(fuso) {
    try {
        new Intl.DateTimeFormat('en-US', { timeZone: fuso });
        return true;
    }
    catch {
        return false;
    }
}
/** Uma data de calendário válida de verdade — '2026-02-30' não é. */
export function dataValida(data) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(data))
        return false;
    return adicionarDias(data, 0) === data;
}
// ---------------------------------------------------------------------
// O dia da GRADE — a arena que fecha depois da meia-noite.
// ---------------------------------------------------------------------
/** Os minutos de um dia de relógio. */
export const MINUTOS_DO_DIA = 24 * 60;
/**
 * Até onde a madrugada pertence ao DIA ANTERIOR, em minutos do relógio:
 * 120 para quem fecha às 02:00, zero para quem fecha até a meia-noite.
 *
 * É o corte do dia da grade. A locação da 01:00 de domingo é da noite de
 * sábado — está na grade de sábado, conta no resumo de sábado e é o "hoje"
 * da recepção que ainda está lá à 01:00. Da 02:00 em diante, é domingo.
 */
export function corteDoDia(janela) {
    return Math.max(0, minutosDe(janela.fim) - MINUTOS_DO_DIA);
}
/** "25:30" → "01:30"; "24:00" → "00:00". A hora da grade lida no relógio. */
export function horaNoRelogio(hora) {
    return horaDe(minutosDe(hora) % MINUTOS_DO_DIA);
}
/**
 * Uma hora da grade de `dia` no calendário: ("2026-10-10", "25:00") é a
 * 01:00 de 2026-10-11.
 *
 * A API e o banco falam SÓ calendário — data e hora reais, "00:00" a
 * "23:59". A hora estendida existe na tela, para a grade de sábado poder
 * seguir até as 02:00; ela vira calendário aqui, antes de sair.
 */
export function noCalendario(dia, hora) {
    const minutos = minutosDe(hora);
    if (minutos < MINUTOS_DO_DIA)
        return { data: dia, hora };
    return {
        data: adicionarDias(dia, Math.floor(minutos / MINUTOS_DO_DIA)),
        hora: horaDe(minutos % MINUTOS_DO_DIA),
    };
}
/**
 * O inverso de `noCalendario`: data e hora do calendário no dia da grade.
 * A 01:00 de domingo, numa arena que fecha às 02:00, é a "25:00" de sábado.
 */
export function naGrade(data, hora, janela) {
    const minutos = minutosDe(hora);
    if (minutos < corteDoDia(janela)) {
        return { dia: adicionarDias(data, -1), hora: horaDe(minutos + MINUTOS_DO_DIA) };
    }
    return { dia: data, hora };
}
/** O dia da grade a que um instante pertence — o "hoje" da recepção, à 01:00 ainda é ontem. */
export function diaDaGrade(instante, janela, fuso) {
    const partes = partesNoFuso(instante, fuso);
    return naGrade(partes.data, partes.hora, janela).dia;
}
/**
 * Os minutos de um instante contados da meia-noite que abre `dia`, no
 * relógio de parede: a 01:00 do dia seguinte é 1500. A geometria da grade
 * mede assim, e é por isso que a madrugada cabe no fim dela.
 */
export function minutosNaGrade(instante, dia, fuso) {
    const partes = partesNoFuso(instante, fuso);
    return diasEntre(dia, partes.data) * MINUTOS_DO_DIA + partes.minutosDoDia;
}
/**
 * O dia da grade como intervalo de instantes `[inicio, fim)`: do corte de
 * `dia` ao corte do dia seguinte. Para quem fecha até a meia-noite é o
 * próprio `intervaloDoDia`; para quem fecha às 02:00, de 02:00 a 02:00.
 */
export function intervaloDaGrade(dia, janela, fuso) {
    const corte = horaDe(corteDoDia(janela));
    return {
        inicio: instanteLocal(dia, corte, fuso),
        fim: instanteLocal(adicionarDias(dia, 1), corte, fuso),
    };
}
//# sourceMappingURL=index.js.map